import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import { Search, Calendar, ChevronDown, RefreshCw } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';

function AdminGoals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page')) || 1;
    const initialWorkspaceId = searchParams.get('workspaceId') || '';
    const initialPreset = searchParams.get('datePreset') || 'last30';
    const initialStartDate = searchParams.get('startDate') || '';
    const initialEndDate = searchParams.get('endDate') || '';
    const initialSearch = searchParams.get('search') || '';
    const initialSortOrder = searchParams.get('order') || 'DESC';

    const [goals, setGoals] = useState([]);
    const [totalGoals, setTotalGoals] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    // Filters
    const [searchTerm, setSearchTerm] = useState(initialSearch);
    const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
    const [datePreset, setDatePreset] = useState(initialPreset);
    const [customStartDate, setCustomStartDate] = useState(initialStartDate);
    const [customEndDate, setCustomEndDate] = useState(initialEndDate);
    const [sortOrder, setSortOrder] = useState(initialSortOrder);

    // Workspaces searchable dropdown state
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [wsSearch, setWsSearch] = useState('');
    const [isWsDropdownOpen, setIsWsDropdownOpen] = useState(false);
    const wsDropdownRef = useRef(null);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Load workspaces on boot
    useEffect(() => {
        const loadWorkspaces = async () => {
            try {
                const response = await adminManagementService.getWorkspaces({ limit: 1000 });
                if (response && response.data) {
                    setWorkspaces(response.data);
                    // If initial workspaceId exists, set selected workspace
                    if (initialWorkspaceId) {
                        const ws = response.data.find(w => w.id === initialWorkspaceId);
                        if (ws) setSelectedWorkspace(ws);
                    }
                }
            } catch (error) {
                console.error('Failed to load workspaces', error);
            }
        };
        loadWorkspaces();
    }, [initialWorkspaceId]);

    // Handle clicking outside workspace dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wsDropdownRef.current && !wsDropdownRef.current.contains(event.target)) {
                setIsWsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Date preset helper
    const getDateRangeFromPreset = useCallback((preset) => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        
        let startDate = '';
        let endDate = '';
        
        switch (preset) {
            case 'today':
                const startOfToday = new Date(today);
                startOfToday.setHours(0, 0, 0, 0);
                startDate = startOfToday.toISOString().split('T')[0];
                endDate = today.toISOString().split('T')[0];
                break;
            case 'last7':
                const last7Days = new Date(today);
                last7Days.setDate(today.getDate() - 6);
                last7Days.setHours(0, 0, 0, 0);
                startDate = last7Days.toISOString().split('T')[0];
                endDate = today.toISOString().split('T')[0];
                break;
            case 'last30':
                const last30Days = new Date(today);
                last30Days.setDate(today.getDate() - 29);
                last30Days.setHours(0, 0, 0, 0);
                startDate = last30Days.toISOString().split('T')[0];
                endDate = today.toISOString().split('T')[0];
                break;
            case 'thisMonth':
                const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                startDate = startOfMonth.toISOString().split('T')[0];
                endDate = today.toISOString().split('T')[0];
                break;
            case 'thisYear':
                const startOfYear = new Date(today.getFullYear(), 0, 1);
                startDate = startOfYear.toISOString().split('T')[0];
                endDate = today.toISOString().split('T')[0];
                break;
            case 'allTime':
                startDate = '';
                endDate = '';
                break;
            default:
                break;
        }
        return { startDate, endDate };
    }, []);

    // Load goals
    const loadGoals = useCallback(async () => {
        try {
            setLoading(true);
            const range = datePreset === 'custom'
                ? { startDate: customStartDate, endDate: customEndDate }
                : getDateRangeFromPreset(datePreset);

            const params = {
                page: currentPage,
                limit: 15,
                search: debouncedSearch,
                workspaceId: selectedWorkspace?.id || '',
                startDate: range.startDate,
                endDate: range.endDate,
                sort: 'createdAt',
                order: sortOrder
            };

            const response = await adminManagementService.getGoals(params);
            if (response) {
                setGoals(response.data || []);
                setTotalGoals(response.total || 0);
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('Failed to load goals', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearch, selectedWorkspace?.id, datePreset, customStartDate, customEndDate, sortOrder, getDateRangeFromPreset]);

    useEffect(() => {
        loadGoals();
    }, [loadGoals]);

    // Sync parameters back to URL search params
    const updateUrlParams = (newParams) => {
        const updated = new URLSearchParams(searchParams);
        Object.entries(newParams).forEach(([key, val]) => {
            if (val) {
                updated.set(key, val);
            } else {
                updated.delete(key);
            }
        });
        updated.set('page', '1'); // Reset page to 1 on filter changes
        setSearchParams(updated);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        const updated = new URLSearchParams(searchParams);
        if (val) updated.set('search', val);
        else updated.delete('search');
        updated.set('page', '1');
        setSearchParams(updated);
    };

    const handleWorkspaceSelect = (workspace) => {
        setSelectedWorkspace(workspace);
        setIsWsDropdownOpen(false);
        updateUrlParams({ workspaceId: workspace ? workspace.id : '' });
    };

    const handlePresetChange = (e) => {
        const val = e.target.value;
        setDatePreset(val);
        if (val === 'custom') {
            updateUrlParams({ datePreset: val, startDate: customStartDate, endDate: customEndDate });
        } else {
            updateUrlParams({ datePreset: val, startDate: '', endDate: '' });
        }
    };

    const handleCustomDateChange = (type, val) => {
        if (type === 'start') {
            setCustomStartDate(val);
            updateUrlParams({ datePreset: 'custom', startDate: val, endDate: customEndDate });
        } else {
            setCustomEndDate(val);
            updateUrlParams({ datePreset: 'custom', startDate: customStartDate, endDate: val });
        }
    };

    const handlePageChange = (newPage) => {
        const updated = new URLSearchParams(searchParams);
        updated.set('page', newPage.toString());
        setSearchParams(updated);
    };

    const handleSortToggle = () => {
        const nextOrder = sortOrder === 'DESC' ? 'ASC' : 'DESC';
        setSortOrder(nextOrder);
        const updated = new URLSearchParams(searchParams);
        updated.set('order', nextOrder);
        setSearchParams(updated);
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setDatePreset('last30');
        setCustomStartDate('');
        setCustomEndDate('');
        setSelectedWorkspace(null);
        setWsSearch('');
        setSortOrder('DESC');
        setSearchParams(new URLSearchParams({ page: '1', datePreset: 'last30' }));
    };

    // Filter workspaces list by search term
    const filteredWorkspaces = workspaces.filter(ws =>
        ws.name.toLowerCase().includes(wsSearch.toLowerCase())
    );

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <h2>Test Goals listing ({totalGoals})</h2>
                    <p className="subtitle">View and audit all autonomous test goal definitions</p>
                </div>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="icon-btn" onClick={loadGoals} title="Refresh page data">
                        <RefreshCw size={18} />
                    </button>
                    <button className="admin-cancel-btn" onClick={handleResetFilters}>
                        Reset Filters
                    </button>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="admin-dashboard-header" style={{
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '12px',
                padding: '16px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
                alignItems: 'center'
            }}>
                {/* Search Term */}
                <div className="admin-search-bar" style={{ width: 'auto' }}>
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search Goal ID or text..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>

                {/* Workspace Searchable Dropdown */}
                <div className="workspace-dropdown-container" ref={wsDropdownRef} style={{ position: 'relative' }}>
                    <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                        Workspace Filter
                    </label>
                    <div 
                        className="workspace-selector"
                        onClick={() => setIsWsDropdownOpen(!isWsDropdownOpen)}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#0f172a',
                            border: '1px solid #334155',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            color: selectedWorkspace ? '#f8fafc' : '#94a3b8',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        <span>{selectedWorkspace ? selectedWorkspace.name : 'All Workspaces'}</span>
                        <ChevronDown size={16} />
                    </div>

                    {isWsDropdownOpen && (
                        <div className="workspace-dropdown-menu" style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: '#1e293b',
                            border: '1px solid #334155',
                            borderRadius: '8px',
                            marginTop: '4px',
                            zIndex: 10,
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
                            padding: '8px',
                            maxHeight: '260px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                        }}>
                            <input
                                type="text"
                                placeholder="Search workspace..."
                                value={wsSearch}
                                onChange={(e) => setWsSearch(e.target.value)}
                                style={{
                                    backgroundColor: '#0f172a',
                                    border: '1px solid #334155',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    color: '#f8fafc',
                                    fontSize: '0.85rem',
                                    outline: 'none'
                                }}
                            />
                            <div className="workspace-options-list" style={{
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                            }}>
                                <button
                                    onClick={() => handleWorkspaceSelect(null)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        textAlign: 'left',
                                        color: '#cbd5e1',
                                        padding: '8px 10px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                        backgroundColor: !selectedWorkspace ? '#334155' : 'transparent'
                                    }}
                                >
                                    All Workspaces
                                </button>
                                {filteredWorkspaces.map(ws => (
                                    <button
                                        key={ws.id}
                                        onClick={() => handleWorkspaceSelect(ws)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            textAlign: 'left',
                                            color: '#cbd5e1',
                                            padding: '8px 10px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            backgroundColor: selectedWorkspace?.id === ws.id ? '#334155' : 'transparent'
                                        }}
                                    >
                                        {ws.name}
                                    </button>
                                ))}
                                {filteredWorkspaces.length === 0 && (
                                    <span style={{ color: '#64748b', fontSize: '0.8rem', padding: '8px 10px', textAlign: 'center' }}>
                                        No workspaces found
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Date Filter Preset */}
                <div>
                    <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                        Date Filter
                    </label>
                    <select
                        value={datePreset}
                        onChange={handlePresetChange}
                        style={{
                            backgroundColor: '#0f172a',
                            border: '1px solid #334155',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            color: '#f8fafc',
                            width: '100%',
                            fontSize: '0.9rem',
                            height: '40px',
                            outline: 'none'
                        }}
                    >
                        <option value="today">Today</option>
                        <option value="last7">Last 7 days</option>
                        <option value="last30">Last 30 days</option>
                        <option value="thisMonth">This Month</option>
                        <option value="thisYear">This Year</option>
                        <option value="allTime">All Time</option>
                        <option value="custom">Custom Range</option>
                    </select>
                </div>

                {/* Custom Dates */}
                {datePreset === 'custom' && (
                    <>
                        <div>
                            <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                                Start Date
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '4px 12px', height: '40px' }}>
                                <Calendar size={16} color="#64748b" style={{ marginRight: '8px' }} />
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => handleCustomDateChange('start', e.target.value)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#f8fafc',
                                        outline: 'none',
                                        width: '100%',
                                        fontSize: '0.9rem'
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                                End Date
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '4px 12px', height: '40px' }}>
                                <Calendar size={16} color="#64748b" style={{ marginRight: '8px' }} />
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => handleCustomDateChange('end', e.target.value)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#f8fafc',
                                        outline: 'none',
                                        width: '100%',
                                        fontSize: '0.9rem'
                                    }}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Sort Toggle */}
                <div style={{ alignSelf: 'end' }}>
                    <button 
                        className="admin-cancel-btn" 
                        onClick={handleSortToggle}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', height: '40px' }}
                    >
                        Sort: {sortOrder === 'DESC' ? 'Newest First' : 'Oldest First'}
                    </button>
                </div>
            </div>

            {/* Table */}
            {loading && goals.length === 0 ? (
                <div className="admin-loading">Loading test goals...</div>
            ) : (
                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Goal ID</th>
                                <th>Goal Text</th>
                                <th>Workspace</th>
                                <th>App Name</th>
                                <th>Platform</th>
                                <th>Creation Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {goals.map(goal => (
                                <tr key={goal.id}>
                                    <td>
                                        <code style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                            {goal.id.substring(0, 8)}...
                                        </code>
                                    </td>
                                    <td style={{ maxWidth: '300px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                        <strong>{goal.goalText}</strong>
                                    </td>
                                    <td>{goal.workspace?.name || 'Unknown Workspace'}</td>
                                    <td>{goal.appName}</td>
                                    <td>
                                        <span className={`plan-badge ${goal.platform === 'android' ? 'pro' : 'business'}`}>
                                            {goal.platform.toUpperCase()}
                                        </span>
                                    </td>
                                    <td>{new Date(goal.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <Link 
                                            to={`/admin/goals/${goal.id}`} 
                                            style={{ color: '#38bdf8', fontWeight: 600, textDecoration: 'none' }}
                                        >
                                            View Details
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                            {goals.length === 0 && (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                                        No test goals found matching current criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Pagination 
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    );
}

export default AdminGoals;
